import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Marker, Popup, Tooltip, useMap, LayersControl } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiSearch, FiMapPin, FiTrash2, FiSave, FiInfo,
    FiLayers, FiCheck, FiX, FiNavigation, FiMaximize2, FiActivity, FiCrosshair, FiPlus, FiTarget, FiEdit3, FiLoader,
    FiCalendar, FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown, FiExternalLink, FiFilter, FiDownload,
    FiShield, FiAlertCircle, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import axios from 'axios';
import L from 'leaflet';
import * as turf from '@turf/turf';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// CSS Imports
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// Fix for Leaflet default icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 18;

// Component to handle map view updates
const MapUpdater = ({ center, zoom, forceUpdate }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.flyTo(center, zoom || map.getZoom(), { duration: 1.5 });
            // Crucial for mobile view where container might change size
            setTimeout(() => {
                map.invalidateSize();
            }, 500);
        }
    }, [center, zoom, map, forceUpdate]);
    return null;
};

const GeoFencingAdmin = () => {
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [geofences, setGeofences] = useState([]);
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPolygon, setCurrentPolygon] = useState(null);
    const [manualCoordList, setManualCoordList] = useState([
        { lat: '', lng: '' }, { lat: '', lng: '' }, { lat: '', lng: '' }, { lat: '', lng: '' }
    ]);
    const [formData, setFormData] = useState({ customer_name: '', status: 'active' });
    const [stats, setStats] = useState({ areaSqM: 0, pointCount: 0, centroid: null });
    const [editingId, setEditingId] = useState(null);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [fenceSearchQuery, setFenceSearchQuery] = useState('');
    const featureGroupRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);

    // Tabs state
    const [activeTab, setActiveTab] = useState('activity');
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

    // Pagination/Sorting state for Attendance
    const [attendanceSearch, setAttendanceSearch] = useState('');
    const [attendancePage, setAttendancePage] = useState(1);
    const [attendanceSort, setAttendanceSort] = useState({ key: 'punch_date', direction: 'desc' });

    // Pagination/Sorting state for Perimeters
    const [perimeterSearch, setPerimeterSearch] = useState('');
    const [perimeterPage, setPerimeterPage] = useState(1);
    const [perimeterSort, setPerimeterSort] = useState({ key: 'customer_name', direction: 'asc' });

    const [zoomedImage, setZoomedImage] = useState(null);
    const [imageIndices, setImageIndices] = useState({}); // { 'logId_type': 0 }

    // --- Face Management Tab state ---
    const [faceEmployees, setFaceEmployees] = useState([]);
    const [faceSearch, setFaceSearch] = useState('');
    const [isFaceLoading, setIsFaceLoading] = useState(false);
    const [faceResetTarget, setFaceResetTarget] = useState(null); // employee to reset
    const [isResetting, setIsResetting] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [facePage, setFacePage] = useState(1);
    const [faceSort, setFaceSort] = useState({ key: 'face_registered', direction: 'desc' });

    useEffect(() => {
        setFacePage(1);
    }, [faceSearch]);

    const [faceItemsPerPage, setFaceItemsPerPage] = useState(10);
    const [attendanceItemsPerPage, setAttendanceItemsPerPage] = useState(10);
    const [perimeterItemsPerPage, setPerimeterItemsPerPage] = useState(10);

    const PageSizeSelector = ({ value, onChange }) => (
        <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Show</span>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-3 py-1.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-red-400 outline-none transition-all cursor-pointer min-w-[60px]"
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={9999}>All</option>
                </select>
            </div>
            <span className="text-xs font-medium text-gray-500">entries</span>
        </div>
    );

    const Pagination = ({ currentPage, totalPages, onPageChange }) => {
        if (totalPages <= 1) return null;

        const maxVisible = 4;
        let start = Math.floor((currentPage - 1) / maxVisible) * maxVisible + 1;
        let end = Math.min(start + maxVisible - 1, totalPages);

        const pages = [];
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return (
            <div className="pagination-btns">
                <button
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    className="p-btn"
                >
                    <FiChevronLeft />
                </button>
                {pages.map(p => (
                    <button
                        key={p}
                        onClick={() => onPageChange(p)}
                        className={`p-btn ${currentPage === p ? 'active' : ''}`}
                    >
                        {p}
                    </button>
                ))}
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    className="p-btn"
                >
                    <FiChevronRight />
                </button>
            </div>
        );
    };

    const cycleImage = (logId, type, direction, max) => {
        const key = `${logId}_${type}`;
        const currentIndex = imageIndices[key] || 0;
        let nextIndex = currentIndex + direction;
        if (nextIndex < 0) nextIndex = max - 1;
        if (nextIndex >= max) nextIndex = 0;
        setImageIndices(prev => ({ ...prev, [key]: nextIndex }));
    };

    // --- Face Management Handlers ---
    const fetchFaceEmployees = async () => {
        setIsFaceLoading(true);
        try {
            const res = await api.get('/face/employees-face-status');
            if (res.data && res.data.success) {
                setFaceEmployees(res.data.data || []);
            } else {
                toast.error('Failed to load employee face data');
            }
        } catch (e) {
            toast.error('Could not reach server');
        } finally {
            setIsFaceLoading(false);
        }
    };

    const handleFaceReset = async () => {
        if (!faceResetTarget) return;
        setIsResetting(true);
        try {
            const formData = new FormData();
            formData.append('user_id', faceResetTarget.user_id);
            formData.append('admin_user_id', localStorage.getItem('user_id') || 0);
            const res = await api.post('/face/reset', formData);
            if (res.data && res.data.success) {
                toast.success(`Face reset for ${faceResetTarget.first_name} ${faceResetTarget.last_name}`);
                // Update local list — mark as not registered
                setFaceEmployees(prev =>
                    prev.map(e => e.user_id === faceResetTarget.user_id
                        ? { ...e, face_registered: 0, registered_at: null }
                        : e
                    )
                );
                setShowResetConfirm(false);
                setFaceResetTarget(null);
            } else {
                toast.error(res.data?.message || 'Reset failed');
            }
        } catch (e) {
            toast.error(e.response?.data?.message || 'Server error during reset');
        } finally {
            setIsResetting(false);
        }
    };

    const toggleFaceSort = (key) => {
        setFaceSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Face Management derived data
    const filteredFaces = faceEmployees.filter(e => {
        const q = faceSearch.toLowerCase();
        return !q ||
            `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
            (e.emp_id || '').toLowerCase().includes(q) ||
            (e.designation_name || '').toLowerCase().includes(q);
    });

    const sortedFaces = [...filteredFaces].sort((a, b) => {
        const { key, direction } = faceSort;
        let valA = a[key];
        let valB = b[key];

        if (key === 'face_registered') {
            valA = parseInt(valA) || 0;
            valB = parseInt(valB) || 0;
        } else {
            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    const paginatedFaces = sortedFaces.slice(
        (facePage - 1) * faceItemsPerPage,
        facePage * faceItemsPerPage
    );
    const totalFacePages = Math.ceil(sortedFaces.length / faceItemsPerPage);

    useEffect(() => {
        fetchGeofences();
        detectLocation();
    }, []);

    useEffect(() => {
        fetchAttendanceLogs();
    }, [fromDate, toDate]);

    const fetchGeofences = async () => {
        try {
            const response = await api.get('/geofence');
            if (response.data.status) {
                const rawData = response.data.data.map(item => ({
                    ...item,
                    polygon_coordinates: typeof item.polygon_coordinates === 'string' ? JSON.parse(item.polygon_coordinates) : item.polygon_coordinates
                }));
                setGeofences(rawData);
            }
        } catch (error) {
            toast.error("Failed to load geofences");
        }
    };

    const fetchAttendanceLogs = async () => {
        setIsLoadingLogs(true);
        try {
            const res = await api.get('/geofence/attendance', {
                params: {
                    from_date: fromDate,
                    to_date: toDate
                }
            });
            if (res.data.status) setAttendanceLogs(res.data.data);
        } catch (error) {
            console.error("Failed to load logs", error);
            toast.error("Failed to load logs");
        } finally {
            setIsLoadingLogs(false);
        }
    };


    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (query.length < 3) { setSearchResults([]); return; }

        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                // Using a custom user-agent as per Nominatim Usage Policy
                const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
                    params: {
                        format: 'json',
                        q: query,
                        limit: 8,
                        addressdetails: 1
                    },
                    headers: {
                        'Accept-Language': 'en-US,en;q=0.9',
                    }
                });

                if (response.data.length === 0) {
                    toast.error("No locations found for this query", { id: 'search-err' });
                }
                setSearchResults(response.data);
            } catch (error) {
                console.error("Search Error:", error);
                toast.error("Search service temporarily unavailable");
            } finally {
                setIsSearching(false);
            }
        }, 800);
    };

    const selectLocation = (loc) => {
        const lat = parseFloat(loc.lat);
        const lon = parseFloat(loc.lon);
        setMapCenter([lat, lon]);
        setMapZoom(20); // Deep zoom for building details
        setSearchResults([]);
        setSearchQuery(loc.display_name);
        setSelectedLocation({
            lat,
            lon,
            name: loc.display_name.split(',')[0]
        });

        // Update the first manual input row with the selected location
        const newList = [...manualCoordList];
        newList[0] = { lat: lat.toFixed(6), lng: lon.toFixed(6) };
        setManualCoordList(newList);

        setForceUpdate(prev => prev + 1);
    };

    const handleManualRowChange = (index, field, value) => {
        const newList = [...manualCoordList];
        newList[index][field] = value;

        // Automatically add a new row if we're filling the last one
        if (index === manualCoordList.length - 1 && value.trim() !== '') {
            newList.push({ lat: '', lng: '' });
        }

        setManualCoordList(newList);

        // If both fields in this row are filled, update map
        if (newList[index].lat && newList[index].lng) {
            const lat = parseFloat(newList[index].lat);
            const lng = parseFloat(newList[index].lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                setMapCenter([lat, lng]);
                setMapZoom(20);
                setSelectedLocation({ lat, lon: lng, name: `Node ${index + 1}` });
                setForceUpdate(prev => prev + 1);
            }
        }
    };

    const syncManualNodesToMap = () => {
        const validCoords = manualCoordList
            .map(row => [parseFloat(row.lat), parseFloat(row.lng)])
            .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

        if (validCoords.length < 3) {
            return toast.error("Need at least 3 valid coordinates to form a boundary!");
        }

        updateCurrentPolygon(validCoords);

        // Clear feature group to show the synced version
        if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
            const poly = L.polygon(validCoords, { color: '#ef4444', fillOpacity: 0.3 });
            featureGroupRef.current.addLayer(poly);
        }

        toast.success("Coordinates synced to map!");
    };

    const addNewCoordRow = () => {
        setManualCoordList([...manualCoordList, { lat: '', lng: '' }]);
    };

    const removeCoordRow = (index) => {
        if (manualCoordList.length <= 1) return; // Keep at least one
        const newList = manualCoordList.filter((_, i) => i !== index);
        setManualCoordList(newList);
    };

    const _onCreated = (e) => {
        const { layerType, layer } = e;
        if (layerType === 'polygon') {
            const latlngs = layer.getLatLngs()[0].map(ll => [ll.lat, ll.lng]);
            updateCurrentPolygon(latlngs);
        }
    };

    const _onEdited = (e) => {
        const { layers } = e;
        layers.eachLayer(layer => {
            const latlngs = layer.getLatLngs()[0].map(ll => [ll.lat, ll.lng]);
            updateCurrentPolygon(latlngs);
        });
    };

    const _onDeleted = () => {
        setCurrentPolygon(null);
        setStats({ areaSqM: 0, pointCount: 0, centroid: null });
    };

    const updateCurrentPolygon = (latlngs) => {
        setCurrentPolygon(latlngs);

        // Sync to manual coordinate list
        const newList = latlngs.map(c => ({ lat: c[0].toFixed(6), lng: c[1].toFixed(6) }));
        // Ensure at least 4 rows for consistency
        while (newList.length < 4) newList.push({ lat: '', lng: '' });
        setManualCoordList(newList);

        if (latlngs.length >= 3) {
            try {
                // Leaflet [lat, lng] -> Turf [lng, lat]
                const turfCoords = latlngs.map(c => [c[1], c[0]]);
                turfCoords.push(turfCoords[0]); // Closing loop

                const polygon = turf.polygon([turfCoords]);
                const area = turf.area(polygon);
                const centroid = turf.centroid(polygon);

                setStats({
                    areaSqM: area.toFixed(2),
                    pointCount: latlngs.length,
                    // Turf centroid is [lng, lat], we store as [lng, lat] for DB but used carefully
                    centroid: centroid.geometry.coordinates
                });
            } catch (err) {
                console.error("Turf Calculation Error:", err);
            }
        }
    };

    const saveGeofence = async () => {
        if (!currentPolygon || currentPolygon.length < 3) return toast.error("Draw a polygon first!");
        if (!formData.customer_name) return toast.error("Enter site name!");

        try {
            const payload = {
                ...formData,
                polygon_coordinates: typeof currentPolygon === 'string' ? JSON.parse(currentPolygon) : currentPolygon,
                centroid_lat: stats.centroid[1],
                centroid_lng: stats.centroid[0],
                area_size: stats.areaSqM
            };

            let res;
            if (editingId) {
                // Update existing boundary
                res = await api.put(`/geofence/${editingId}`, payload);
            } else {
                // Create new boundary
                res = await api.post('/geofence', payload);
            }

            if (res.data.status) {
                toast.success(editingId ? "Boundary Updated!" : "Boundary Created!");
                resetCanvas();
                fetchGeofences();
            }
        } catch (error) {
            console.error("Save Error:", error);
            toast.error("Save failed");
        }
    };

    const editGeofence = (fence) => {
        // Clear existing canvas first
        resetCanvas();
        setEditingId(fence.id);

        setFormData({
            customer_name: fence.customer_name,
            status: fence.status
        });

        const coords = fence.polygon_coordinates;
        // Populate manual nodes for form editing
        const newList = coords.map(c => ({ lat: c[0].toString(), lng: c[1].toString() }));
        while (newList.length < 4) newList.push({ lat: '', lng: '' });
        setManualCoordList(newList);

        // Load into Leaflet Draw FeatureGroup so toolbar icons (pencil/trash) work on it
        if (featureGroupRef.current) {
            const latlngs = coords.map(c => [c[0], c[1]]);
            const polygonLayer = L.polygon(latlngs, { color: '#ef4444', fillOpacity: 0.3 });
            featureGroupRef.current.addLayer(polygonLayer);

            // Re-calculate stats
            updateCurrentPolygon(coords);
        }

        // Zoom to the fence
        if (coords.length > 0) {
            setMapCenter([coords[0][0], coords[0][1]]);
            setMapZoom(19);
            setForceUpdate(p => p + 1);
        }

        toast.success(`Editing Site: ${fence.customer_name}`);
    };

    const detectLocation = () => {
        if (!navigator.geolocation) {
            return toast.error("Geolocation not supported");
        }

        toast.loading("Locating...", { id: 'geo' });

        const success = (pos) => {
            const { latitude, longitude } = pos.coords;
            setMapCenter([latitude, longitude]);
            setMapZoom(20);
            setForceUpdate(p => p + 1);
            toast.success("Located via GPS", { id: 'geo' });
        };

        const error = async () => {
            // Fallback to IP Geolocation
            try {
                toast.loading("GPS failed, trying IP...", { id: 'geo' });
                const res = await axios.get('https://ipapi.co/json/');
                if (res.data && res.data.latitude && res.data.longitude) {
                    setMapCenter([res.data.latitude, res.data.longitude]);
                    setMapZoom(16); // Less zoom for IP location as it's approximate
                    setForceUpdate(p => p + 1);
                    toast.success(`Located near ${res.data.city} (IP)`, { id: 'geo' });
                } else {
                    throw new Error("Invalid IP data");
                }
            } catch (err) {
                toast.error("Location access denied & IP failed", { id: 'geo' });
            }
        };

        navigator.geolocation.getCurrentPosition(success, error, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        });
    };

    const resetCanvas = () => {
        setCurrentPolygon(null);
        setEditingId(null);
        setFormData({ customer_name: '', status: 'active' });
        setStats({ areaSqM: 0, pointCount: 0, centroid: null });
        setManualCoordList([
            { lat: '', lng: '' }, { lat: '', lng: '' }, { lat: '', lng: '' }, { lat: '', lng: '' }
        ]);
        if (featureGroupRef.current) featureGroupRef.current.clearLayers();
    };

    const deleteFence = async (id) => {
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/geofence/${idToDelete}`);
            toast.success("Deleted successfully");
            fetchGeofences();
            setShowDeleteModal(false);
            setIdToDelete(null);
        } catch (error) {
            toast.error("Delete failed");
            setShowDeleteModal(false);
        }
    };

    // Helper functions for Filtering, Sorting, and Pagination
    const handleSort = (type, key) => {
        if (type === 'activity') {
            setAttendanceSort(prev => ({
                key,
                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
            setAttendancePage(1);
        } else if (type === 'face_mgmt') {
            setFaceSort(prev => ({
                key,
                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
            setFacePage(1);
        } else {
            setPerimeterSort(prev => ({
                key,
                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
            setPerimeterPage(1);
        }
    };

    const renderSortIcon = (type, key) => {
        let sort;
        if (type === 'activity') sort = attendanceSort;
        else if (type === 'face_mgmt') sort = faceSort;
        else sort = perimeterSort;

        if (sort.key !== key) return <FiChevronUp className="sort-icon opacity-20" />;
        return sort.direction === 'asc' ? <FiChevronUp className="sort-icon" /> : <FiChevronDown className="sort-icon" />;
    };

    const getFilteredData = (type) => {
        if (type === 'activity') {
            return attendanceLogs.filter(log => {
                const query = attendanceSearch.toLowerCase();
                return (
                    `${log.first_name} ${log.last_name}`.toLowerCase().includes(query) ||
                    (log.designation_name || '').toLowerCase().includes(query) ||
                    (log.site_name || '').toLowerCase().includes(query) ||
                    (log.status == 1 ? 'punched in' : 'punched out').toLowerCase().includes(query)
                );
            }).sort((a, b) => {
                const { key, direction } = attendanceSort;
                let valA = a[key] || '';
                let valB = b[key] || '';
                if (key === 'first_name') {
                    valA = `${a.first_name} ${a.last_name}`;
                    valB = `${b.first_name} ${b.last_name}`;
                }
                if (valA < valB) return direction === 'asc' ? -1 : 1;
                if (valA > valB) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        } else {
            return geofences.filter(f =>
                f.customer_name.toLowerCase().includes(perimeterSearch.toLowerCase())
            ).sort((a, b) => {
                const { key, direction } = perimeterSort;
                let valA = a[key];
                let valB = b[key];
                if (key === 'points') {
                    valA = a.polygon_coordinates.length;
                    valB = b.polygon_coordinates.length;
                }
                if (valA < valB) return direction === 'asc' ? -1 : 1;
                if (valA > valB) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
    };

    const getPaginatedData = (type) => {
        const filtered = getFilteredData(type);
        if (type === 'activity') {
            return filtered.slice((attendancePage - 1) * attendanceItemsPerPage, attendancePage * attendanceItemsPerPage);
        } else {
            return filtered.slice((perimeterPage - 1) * perimeterItemsPerPage, perimeterPage * perimeterItemsPerPage);
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="gf-dashboard">
            <div className="gf-layout">
                {/* Left: Map Section */}
                <div className="gf-map-container">
                    {/* HUD Search - Moved down slightly to avoid layer switch overlap */}
                    <div className={`gf-search-hud ${isMobileSearchOpen ? 'mobile-open' : ''}`}>
                        <div className="hud-input-group">
                            <button
                                className="hud-toggle-btn mobile-only"
                                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                                title="Toggle Search"
                            >
                                <FiSearch />
                            </button>
                            <FiSearch className="hud-icon desktop-only" />
                            <input
                                type="text"
                                placeholder="Search location..."
                                value={searchQuery}
                                onChange={handleSearch}
                                className={!isMobileSearchOpen ? 'mobile-hidden' : ''}
                            />
                            {isSearching && <div className="hud-loader" />}
                        </div>
                        <AnimatePresence>
                            {(searchResults.length > 0 || (searchQuery.length >= 3 && !isSearching)) && (
                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="hud-results">
                                    {searchResults.length > 0 ? (
                                        searchResults.map((loc, i) => (
                                            <div key={i} className="hud-item" onClick={() => selectLocation(loc)}>
                                                <FiMapPin className="item-icon" />
                                                <div className="item-text">
                                                    <span className="main">{loc.display_name.split(',')[0]}</span>
                                                    <span className="secondary">{loc.display_name.split(',').slice(1).join(',')}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="hud-item empty">
                                            <FiInfo className="item-icon" />
                                            <span>No exact matches found.</span>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Locate Me Floating Button */}
                    <button className="locate-me-btn" onClick={detectLocation} title="Go to My Location">
                        <FiTarget />
                    </button>

                    <MapContainer
                        center={mapCenter}
                        zoom={mapZoom}
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={false}
                        maxZoom={22}
                    >
                        <LayersControl position="topleft">
                            <LayersControl.BaseLayer checked name="Street Map">
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    maxNativeZoom={19}
                                    maxZoom={22}
                                />
                            </LayersControl.BaseLayer>
                            <LayersControl.BaseLayer name="Satellite View">
                                <TileLayer
                                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                    attribution="Tiles &copy; Esri &mdash; Source: Esri"
                                    maxNativeZoom={19}
                                    maxZoom={22}
                                />
                            </LayersControl.BaseLayer>
                            <LayersControl.BaseLayer name="Google Satellite">
                                <TileLayer
                                    url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                                    maxNativeZoom={20}
                                    maxZoom={22}
                                />
                            </LayersControl.BaseLayer>
                        </LayersControl>
                        <MapUpdater center={mapCenter} zoom={mapZoom} forceUpdate={forceUpdate} />

                        {/* Current Location Pulsing Marker */}
                        {mapCenter && (
                            <Marker
                                position={mapCenter}
                                icon={L.divIcon({
                                    className: 'custom-div-icon',
                                    html: '<div class="pulse-container"><div class="pulse-dot"></div><div class="pulse-ring"></div></div>',
                                    iconSize: [30, 30],
                                    iconAnchor: [15, 15]
                                })}
                            />
                        )}

                        <FeatureGroup ref={featureGroupRef}>
                            <EditControl
                                position="topright"
                                onCreated={_onCreated}
                                onEdited={_onEdited}
                                onDeleted={_onDeleted}
                                draw={{
                                    rectangle: false, circle: false, circlemarker: false, marker: false, polyline: false,
                                    polygon: {
                                        shapeOptions: { color: '#ef4444', fillOpacity: 0.3 }
                                    }
                                }}
                            />
                        </FeatureGroup>

                        {geofences.filter(f => f.id !== editingId).map(f => (
                            <Polygon
                                key={f.id}
                                positions={f.polygon_coordinates}
                                bubblingMouseEvents={false}
                                interactive={true}
                                pathOptions={{
                                    color: f.status === 'active' ? '#10b981' : '#10b981', // Always green for saved, gray only if archived
                                    opacity: 0.8,
                                    fillOpacity: 0.1
                                }}
                                eventHandlers={{
                                    click: () => editGeofence(f)
                                }}
                            />
                        ))}
                    </MapContainer>

                    {/* Map Overlays */}
                    <div className="map-badge">
                        <FiNavigation className="me-1" /> LIVE ENGINE
                    </div>
                </div>

                {/* Right: Control Panel */}
                <div className="gf-sidebar">
                    <div className="sidebar-header">
                        <div className="header-icon"><FiLayers /></div>
                        <div>
                            <h3>Geo-Fencing Console</h3>
                            <p>Spatial Perimeter Management</p>
                        </div>
                    </div>

                    <div className="gf-card">
                        <div className="card-label"><FiActivity className="me-1" /> SITE IDENTITY</div>
                        <div className="form-group">
                            <label>CUSTOMER / SITE NAME</label>
                            <input
                                type="text"
                                placeholder="e.g. Enova Tech Hub"
                                value={formData.customer_name}
                                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>ENFORCEMENT STATUS</label>
                            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                                <option value="active">Active Monitoring</option>
                                <option value="inactive">Suspended</option>
                            </select>
                        </div>
                    </div>

                    <div className="gf-card">
                        <div className="card-header-flex">
                            <div className="card-label"><FiCrosshair className="me-1" /> DIRECT NODE ENTRY</div>
                            <button className="btn-add-row" onClick={addNewCoordRow} title="Add Point Row">
                                <FiPlus />
                            </button>
                        </div>
                        <div className="coord-rows-container">
                            {manualCoordList.map((row, idx) => (
                                <div key={idx} className="coord-row-item mb-2">
                                    <div className="coord-grid">
                                        <div className="form-group mb-0">
                                            <label>LATITUDE {idx + 1}</label>
                                            <input
                                                type="text"
                                                placeholder="00.0000"
                                                value={row.lat}
                                                onChange={(e) => handleManualRowChange(idx, 'lat', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group mb-0">
                                            <label>LONGITUDE {idx + 1}</label>
                                            <input
                                                type="text"
                                                placeholder="00.0000"
                                                value={row.lng}
                                                onChange={(e) => handleManualRowChange(idx, 'lng', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    {manualCoordList.length > 1 && (
                                        <button className="btn-row-remove" onClick={() => removeCoordRow(idx)}>
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            className="btn-sync-coords"
                            onClick={syncManualNodesToMap}
                        >
                            <FiCheck className="me-2" /> Sync Nodes to Map
                        </button>
                    </div>

                    <div className="gf-card dark">
                        <div className="card-label"><FiInfo className="me-1" /> SPATIAL METRICS</div>
                        <div className="metric-row">
                            <div className="metric">
                                <span className="label">Total Area</span>
                                <span className="value">{stats.areaSqM} <small>m²</small></span>
                            </div>
                            <div className="metric">
                                <span className="label">Nodes</span>
                                <span className="value">{stats.pointCount}</span>
                            </div>
                        </div>
                        <div className="metric mt-3">
                            <span className="label">Centroid Coordinates</span>
                            <span className="value coords">
                                {stats.centroid ? `${stats.centroid[1].toFixed(5)}, ${stats.centroid[0].toFixed(5)}` : '--.--- , --.---'}
                            </span>
                        </div>
                    </div>


                    <div className="sidebar-footer">
                        <button className="btn-save" onClick={saveGeofence} disabled={!currentPolygon || !formData.customer_name}>
                            <FiSave className="me-2" /> Commit Boundary
                        </button>
                        <button className="btn-clear" onClick={resetCanvas}>
                            <FiX className="me-2" /> Reset Canvas
                        </button>
                    </div>

                </div>
            </div>

            {/* Tabbed Content Section */}
            <div className="gf-tabs-container">
                <div className="tabs-header">
                    <button
                        className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
                        onClick={() => setActiveTab('activity')}
                    >
                        <FiActivity className="icon" />
                        <span>Geo-Attendance Activity</span>
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'perimeters' ? 'active' : ''}`}
                        onClick={() => setActiveTab('perimeters')}
                    >
                        <FiTarget className="icon" />
                        <span>Saved Perimeters</span>
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'face_mgmt' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('face_mgmt');
                            if (faceEmployees.length === 0) fetchFaceEmployees();
                        }}
                    >
                        <FiShield className="icon" />
                        <span>Face Management</span>
                    </button>
                </div>

                <div className="tab-body">
                    <AnimatePresence mode="wait">
                        {activeTab === 'face_mgmt' && (
                            <motion.div
                                key="face_mgmt"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="tab-pane"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <FiShield className="text-red-600" /> Face Biometric Management
                                    </h3>
                                    <button
                                        onClick={fetchFaceEmployees}
                                        className="refresh-btn-pill"
                                        title="Refresh Employees"
                                    >
                                        <FiLoader className={isFaceLoading ? 'animate-spin' : ''} />
                                    </button>
                                </div>

                                {/* Search & Page Size */}
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                    <div className="relative max-w-sm w-full">
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search employee..."
                                            value={faceSearch}
                                            onChange={e => setFaceSearch(e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none"
                                        />
                                    </div>
                                    <PageSizeSelector value={faceItemsPerPage} onChange={setFaceItemsPerPage} />
                                </div>

                                {isFaceLoading ? (
                                    <div className="flex items-center justify-center py-16 text-gray-400">
                                        <FiLoader className="animate-spin w-8 h-8 mr-3" /> Loading employees...
                                    </div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table className="modern-table v2">
                                            <thead>
                                                <tr>
                                                    <th className="w-16 text-center">S.No</th>
                                                    <th className="sortable text-left" onClick={() => handleSort('face_mgmt', 'first_name')}>
                                                        Employee {renderSortIcon('face_mgmt', 'first_name')}
                                                    </th>
                                                    <th className="sortable text-center" onClick={() => handleSort('face_mgmt', 'emp_id')}>
                                                        Emp ID {renderSortIcon('face_mgmt', 'emp_id')}
                                                    </th>
                                                    <th className="sortable text-center" onClick={() => handleSort('face_mgmt', 'designation_name')}>
                                                        Designation {renderSortIcon('face_mgmt', 'designation_name')}
                                                    </th>
                                                    <th className="sortable text-center" onClick={() => handleSort('face_mgmt', 'face_registered')}>
                                                        Status {renderSortIcon('face_mgmt', 'face_registered')}
                                                    </th>
                                                    <th className="sortable text-center" onClick={() => handleSort('face_mgmt', 'registered_at')}>
                                                        Registered On {renderSortIcon('face_mgmt', 'registered_at')}
                                                    </th>
                                                    <th className="text-right">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedFaces.map((emp, idx) => (
                                                    <tr key={emp.user_id} className="fence-row">
                                                        <td className="text-center font-bold text-gray-400 text-xs border-r border-gray-50">
                                                            {((facePage - 1) * faceItemsPerPage) + idx + 1}
                                                        </td>
                                                        <td className="text-left">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold overflow-hidden border border-red-200">
                                                                    {emp.profile_image ? (
                                                                        <img
                                                                            src={`${api.defaults.baseURL}/../../uploads/profile/${emp.profile_image}`}
                                                                            alt=""
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src = `https://ui-avatars.com/api/?name=${emp.first_name}+${emp.last_name}&background=fee2e2&color=dc2626`;
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-[10px]">{emp.first_name?.[0]}{emp.last_name?.[0]}</span>
                                                                    )}
                                                                </div>
                                                                <span className="font-bold text-sm text-gray-900">{emp.first_name} {emp.last_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center font-mono text-sm text-slate-700">{emp.emp_id || '—'}</td>
                                                        <td className="text-center text-gray-500">{emp.designation_name || '—'}</td>
                                                        <td className="text-center">
                                                            {parseInt(emp.face_registered) === 1 ? (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">
                                                                    <FiShield className="w-3 h-3" /> Registered
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 text-[10px] font-bold rounded-full">
                                                                    <FiAlertCircle className="w-3 h-3" /> Not Registered
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="font-mono text-[10px] text-gray-500 bg-gray-50 p-1.5 rounded-lg border border-gray-100 max-w-[100px] mx-auto">
                                                                {emp.registered_at
                                                                    ? new Date(emp.registered_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                                    : '—'
                                                                }
                                                            </div>
                                                        </td>
                                                        <td className="text-right">
                                                            {parseInt(emp.face_registered) === 1 ? (
                                                                <button
                                                                    onClick={() => { setFaceResetTarget(emp); setShowResetConfirm(true); }}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-bold transition-colors"
                                                                >
                                                                    <FiTrash2 className="w-3 h-3" /> Reset
                                                                </button>
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">No action</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}

                                                {paginatedFaces.length === 0 && (
                                                    <tr><td colSpan={6} className="px-4 py-16 text-center text-gray-400">
                                                        <FiShield className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                        <p className="text-sm font-medium">No matching employees found.</p>
                                                        <p className="text-xs opacity-70">Try adjusting your search or register new faces.</p>
                                                    </td></tr>
                                                )}
                                            </tbody>
                                        </table>

                                        {totalFacePages > 1 && (
                                            <div className="pagination-wrapper !py-4 px-6 border-t border-gray-100 bg-gray-50/30">
                                                <div className="pagination-info">
                                                    Showing {((facePage - 1) * faceItemsPerPage) + 1} to {Math.min(facePage * faceItemsPerPage, filteredFaces.length)} of {filteredFaces.length}
                                                </div>
                                                <Pagination
                                                    currentPage={facePage}
                                                    totalPages={totalFacePages}
                                                    onPageChange={setFacePage}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Reset Confirmation Modal */}
                                {showResetConfirm && faceResetTarget && (
                                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                                        <motion.div
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
                                        >
                                            <div className="text-center mb-4">
                                                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                    <FiTrash2 className="w-7 h-7 text-red-600" />
                                                </div>
                                                <h3 className="text-lg font-bold text-gray-800">Reset Face Biometrics?</h3>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    This will remove the registered face of
                                                    <span className="font-bold text-gray-800"> {faceResetTarget.first_name} {faceResetTarget.last_name}</span>.
                                                    They will need to re-register before they can punch attendance.
                                                </p>
                                            </div>
                                            <div className="flex gap-3 mt-5">
                                                <button
                                                    onClick={() => { setShowResetConfirm(false); setFaceResetTarget(null); }}
                                                    className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleFaceReset}
                                                    disabled={isResetting}
                                                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
                                                >
                                                    {isResetting ? <FiLoader className="animate-spin w-4 h-4" /> : <FiTrash2 className="w-4 h-4" />}
                                                    {isResetting ? 'Resetting...' : 'Yes, Reset'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {activeTab === 'activity' && (
                            <motion.div
                                key="activity"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="tab-pane"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                    <div className="relative max-w-sm w-full">
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search logs..."
                                            value={attendanceSearch}
                                            onChange={(e) => {
                                                setAttendanceSearch(e.target.value);
                                                setAttendancePage(1);
                                            }}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none"
                                        />
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="date-navigation-pill">
                                            <button className="nav-arrow" onClick={() => {
                                                const d1 = new Date(fromDate);
                                                const d2 = new Date(toDate);
                                                d1.setDate(d1.getDate() - 1);
                                                d2.setDate(d2.getDate() - 1);
                                                setFromDate(d1.toISOString().split('T')[0]);
                                                setToDate(d2.toISOString().split('T')[0]);
                                            }}>
                                                <FiChevronLeft />
                                            </button>

                                            <div className="date-picker-group">
                                                <div className="date-input-box" onClick={() => fromDateRef.current?.showPicker()}>
                                                    <div className="date-label">From:</div>
                                                    <input
                                                        ref={fromDateRef}
                                                        type="date"
                                                        value={fromDate}
                                                        onChange={(e) => setFromDate(e.target.value)}
                                                    />
                                                    <div className="date-display">
                                                        {new Date(fromDate).toLocaleDateString('en-GB').replace(/\//g, '-')}
                                                        <FiCalendar className="cal-icon" />
                                                    </div>
                                                </div>

                                                <div className="date-separator">|</div>

                                                <div className="date-input-box" onClick={() => toDateRef.current?.showPicker()}>
                                                    <div className="date-label">To:</div>
                                                    <input
                                                        ref={toDateRef}
                                                        type="date"
                                                        value={toDate}
                                                        onChange={(e) => setToDate(e.target.value)}
                                                    />
                                                    <div className="date-display">
                                                        {new Date(toDate).toLocaleDateString('en-GB').replace(/\//g, '-')}
                                                        <FiCalendar className="cal-icon" />
                                                    </div>
                                                </div>
                                            </div>

                                            <button className="nav-arrow" onClick={() => {
                                                const d1 = new Date(fromDate);
                                                const d2 = new Date(toDate);
                                                d1.setDate(d1.getDate() + 1);
                                                d2.setDate(d2.getDate() + 1);
                                                setFromDate(d1.toISOString().split('T')[0]);
                                                setToDate(d2.toISOString().split('T')[0]);
                                            }}>
                                                <FiChevronRight />
                                            </button>
                                        </div>

                                        <button onClick={fetchAttendanceLogs} className="refresh-btn-pill" title="Refresh Logs" disabled={isLoadingLogs}>
                                            <FiLoader className={isLoadingLogs ? 'animate-spin' : ''} />
                                        </button>
                                        <PageSizeSelector value={attendanceItemsPerPage} onChange={setAttendanceItemsPerPage} />
                                    </div>
                                </div>

                                <div className="table-wrapper">
                                    <table className="modern-table v2">
                                        <thead>
                                            <tr>
                                                <th className="w-16 text-center">S.No</th>
                                                <th onClick={() => handleSort('activity', 'first_name')} className="sortable text-left">
                                                    Employee {renderSortIcon('activity', 'first_name')}
                                                </th>
                                                <th onClick={() => handleSort('activity', 'designation_name')} className="sortable text-center">
                                                    Designation {renderSortIcon('activity', 'designation_name')}
                                                </th>
                                                <th onClick={() => handleSort('activity', 'punch_date')} className="sortable text-center">
                                                    Date {renderSortIcon('activity', 'punch_date')}
                                                </th>
                                                <th className="text-center">Punch In/Out</th>
                                                <th className="text-center">Face Capture</th>
                                                <th onClick={() => handleSort('activity', 'site_name')} className="sortable text-left">
                                                    Site Location {renderSortIcon('activity', 'site_name')}
                                                </th>
                                                <th onClick={() => handleSort('activity', 'status')} className="sortable text-center">
                                                    Status {renderSortIcon('activity', 'status')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getPaginatedData('activity').length > 0 ? (
                                                getPaginatedData('activity').map((log, idx) => (
                                                    <tr key={log.id}>
                                                        <td className="text-center font-bold text-gray-400 text-xs border-r border-gray-50">
                                                            {((attendancePage - 1) * attendanceItemsPerPage) + idx + 1}
                                                        </td>
                                                        <td className="text-left">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-9 h-9 bg-red-50 text-red-600 rounded-xl flex items-center justify-center font-bold text-xs border border-red-100 overflow-hidden">
                                                                    {log.profile_image ? (
                                                                        <img src={log.profile_image} alt="" className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <span>{log.first_name?.[0]}{log.last_name?.[0]}</span>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <div className="font-bold text-sm text-gray-900">{log.first_name} {log.last_name}</div>
                                                                    <div className="text-[10px] text-gray-400 font-mono">#{log.emp_id}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center"><span className="text-xs font-semibold text-gray-600 px-2 py-1 bg-gray-50 rounded-md border border-gray-100">{log.designation_name || 'Staff'}</span></td>
                                                        <td className="text-center"><span className="text-xs text-gray-500 font-medium">{new Date(log.punch_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></td>
                                                        <td className="text-center">
                                                            <div className="flex flex-col items-center gap-1">
                                                                {log.punch_in && (
                                                                    <span className="punch-time in">
                                                                        {log.punch_in.split(',').map(time => new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })).join(', ')}
                                                                    </span>
                                                                )}
                                                                {log.punch_out && (
                                                                    <span className="punch-time out">
                                                                        {log.punch_out.split(',').map(time => new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })).join(', ')}
                                                                    </span>
                                                                )}
                                                                {!log.punch_in && !log.punch_out && <span className="text-gray-300 italic text-[10px]">No Activity</span>}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="flex justify-center gap-3">
                                                                {/* Punch In Images */}
                                                                <div className="capture-bundle">
                                                                    <div
                                                                        className={`face-capture-thumbnail ${log.in_face_images?.length > 0 ? 'cursor-pointer' : ''}`}
                                                                        onClick={() => log.in_face_images?.length > 0 && setZoomedImage(log.in_face_images[imageIndices[`${log.id}_in`] || 0])}
                                                                        title="Click to Zoom"
                                                                    >
                                                                        {log.in_face_images?.length > 0 ? (
                                                                            <img src={log.in_face_images[imageIndices[`${log.id}_in`] || 0]} alt="In" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            <div className="no-face-placeholder">In</div>
                                                                        )}
                                                                    </div>
                                                                    {log.in_face_images?.length > 1 && (
                                                                        <div className="capture-nav">
                                                                            <button onClick={() => cycleImage(log.id, 'in', -1, log.in_face_images.length)}><FiChevronLeft /></button>
                                                                            <span className="text-[9px] font-bold text-gray-500">{(imageIndices[`${log.id}_in`] || 0) + 1}</span>
                                                                            <button onClick={() => cycleImage(log.id, 'in', 1, log.in_face_images.length)}><FiChevronRight /></button>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Punch Out Images */}
                                                                <div className="capture-bundle">
                                                                    <div
                                                                        className={`face-capture-thumbnail ${log.out_face_images?.length > 0 ? 'cursor-pointer' : ''}`}
                                                                        onClick={() => log.out_face_images?.length > 0 && setZoomedImage(log.out_face_images[imageIndices[`${log.id}_out`] || 0])}
                                                                        title="Click to Zoom"
                                                                    >
                                                                        {log.out_face_images?.length > 0 ? (
                                                                            <img src={log.out_face_images[imageIndices[`${log.id}_out`] || 0]} alt="Out" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            <div className="no-face-placeholder">Out</div>
                                                                        )}
                                                                    </div>
                                                                    {log.out_face_images?.length > 1 && (
                                                                        <div className="capture-nav">
                                                                            <button onClick={() => cycleImage(log.id, 'out', -1, log.out_face_images.length)}><FiChevronLeft /></button>
                                                                            <span className="text-[9px] font-bold text-gray-500">{(imageIndices[`${log.id}_out`] || 0) + 1}</span>
                                                                            <button onClick={() => cycleImage(log.id, 'out', 1, log.out_face_images.length)}><FiChevronRight /></button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-left">
                                                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                                                                <FiMapPin className="text-red-500" />
                                                                {log.site_name || 'Unassigned Site'}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`status-badge-v2 ${log.status == 1 ? 'active' : 'complete'}`}>
                                                                {log.status == 1 ? 'Punched In' : 'Punched Out'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-20">
                                                        <div className="flex flex-col items-center text-gray-400">
                                                            <FiActivity size={40} className="mb-2 opacity-20" />
                                                            <p className="italic text-sm">No activity records found for this criteria.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {getFilteredData('activity').length > attendanceItemsPerPage && (
                                    <div className="pagination-wrapper">
                                        <div className="pagination-info">
                                            Showing {((attendancePage - 1) * attendanceItemsPerPage) + 1} to {Math.min(attendancePage * attendanceItemsPerPage, getFilteredData('activity').length)} of {getFilteredData('activity').length} entries
                                        </div>
                                        <Pagination
                                            currentPage={attendancePage}
                                            totalPages={Math.ceil(getFilteredData('activity').length / attendanceItemsPerPage)}
                                            onPageChange={setAttendancePage}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {activeTab === 'perimeters' && (
                            <motion.div
                                key="perimeters"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="tab-pane"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                    <div className="relative max-w-sm w-full">
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search perimeters..."
                                            value={perimeterSearch}
                                            onChange={(e) => {
                                                setPerimeterSearch(e.target.value);
                                                setPerimeterPage(1);
                                            }}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none"
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <button onClick={fetchGeofences} className="refresh-btn-pill" title="Refresh Perimeters">
                                            <FiLoader />
                                        </button>
                                        <PageSizeSelector value={perimeterItemsPerPage} onChange={setPerimeterItemsPerPage} />
                                    </div>
                                </div>

                                <div className="table-wrapper">
                                    <table className="modern-table v2">
                                        <thead>
                                            <tr>
                                                <th className="w-16 text-center">S.No</th>
                                                <th onClick={() => handleSort('perimeters', 'customer_name')} className="sortable text-left">
                                                    Perimeter Name {renderSortIcon('perimeters', 'customer_name')}
                                                </th>
                                                <th onClick={() => handleSort('perimeters', 'area_size')} className="sortable text-center">
                                                    Area Size (m²) {renderSortIcon('perimeters', 'area_size')}
                                                </th>
                                                <th onClick={() => handleSort('perimeters', 'points')} className="sortable text-center">
                                                    No. of Points {renderSortIcon('perimeters', 'points')}
                                                </th>
                                                <th className="text-center">Centroid</th>
                                                <th onClick={() => handleSort('perimeters', 'status')} className="sortable text-center">
                                                    Status {renderSortIcon('perimeters', 'status')}
                                                </th>
                                                <th className="text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getPaginatedData('perimeters').length > 0 ? (
                                                getPaginatedData('perimeters').map((fence, idx) => (
                                                    <tr key={fence.id} className="fence-row">
                                                        <td className="text-center font-bold text-gray-400 text-xs border-r border-gray-50">
                                                            {((perimeterPage - 1) * perimeterItemsPerPage) + idx + 1}
                                                        </td>
                                                        <td className="text-left">
                                                            <div className="flex items-center gap-3">
                                                                <div className="perimeter-icon-box">
                                                                    <FiMapPin />
                                                                </div>
                                                                <div>
                                                                    <div className="font-bold text-sm text-gray-900">{fence.customer_name}</div>
                                                                    <div className="text-[10px] text-gray-400 font-mono">ID: {fence.id}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center"><span className="font-mono text-sm font-bold text-slate-700">{fence.area_size} <small className="text-gray-400 ml-1">m²</small></span></td>
                                                        <td className="text-center">
                                                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-xs font-bold border border-slate-200">
                                                                {fence.polygon_coordinates.length} Nodes
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="font-mono text-[10px] text-gray-500 bg-gray-50 p-1.5 rounded-lg border border-gray-100 max-w-[120px] mx-auto">
                                                                {parseFloat(fence.centroid_lat || 0).toFixed(5)}, {parseFloat(fence.centroid_lng || 0).toFixed(5)}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`status-badge-v2 ${fence.status === 'active' ? 'active' : 'complete'}`}>
                                                                {fence.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-right">
                                                            <div className="flex justify-end gap-2">
                                                                <button onClick={() => editGeofence(fence)} className="action-btn edit" title="Edit Perimeter">
                                                                    <FiEdit3 />
                                                                </button>
                                                                <button onClick={() => deleteFence(fence.id)} className="action-btn del" title="Delete Perimeter">
                                                                    <FiTrash2 />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-20">
                                                        <div className="flex flex-col items-center text-gray-400">
                                                            <FiTarget size={40} className="mb-2 opacity-20" />
                                                            <p className="italic text-sm">No perimeters found matching your search.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {getFilteredData('perimeters').length > perimeterItemsPerPage && (
                                    <div className="pagination-wrapper">
                                        <div className="pagination-info">
                                            Showing {((perimeterPage - 1) * perimeterItemsPerPage) + 1} to {Math.min(perimeterPage * perimeterItemsPerPage, getFilteredData('perimeters').length)} of {getFilteredData('perimeters').length} entries
                                        </div>
                                        <Pagination
                                            currentPage={perimeterPage}
                                            totalPages={Math.ceil(getFilteredData('perimeters').length / perimeterItemsPerPage)}
                                            onPageChange={setPerimeterPage}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Custom Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
                                    <FiTrash2 size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Delete</h3>
                                <p className="text-gray-500 mb-6">Are you sure you want to delete this zone? This action cannot be undone.</p>
                                <div className="flex gap-3 w-full">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style>{`
                /* Pulsing Marker */
                .pulse-container {
                    position: relative;
                    width: 20px;
                    height: 20px;
                }
                .pulse-dot {
                    width: 12px;
                    height: 12px;
                    background: #3b82f6;
                    border: 2px solid white;
                    border-radius: 50%;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 10;
                    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
                }
                .pulse-ring {
                    width: 30px;
                    height: 30px;
                    border: 3px solid #3b82f6;
                    border-radius: 50%;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    animation: pulse-animation 2s infinite;
                }
                @keyframes pulse-animation {
                    0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
                    100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
                }

                .gf-dashboard {
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    overflow: visible;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                    height: auto;
                    position: relative;
                    animation: slideUp 0.6s ease-out;
                }
                .gf-layout {
                    display: flex;
                    height: 600px;
                    border-bottom: 1px solid #e2e8f0;
                }
                .gf-attendance-section {
                    padding: 24px;
                    background: #fff;
                }
                .gf-map-container {
                    flex: 1;
                    position: relative;
                    background: #f1f5f9;
                    border-right: 1px solid #e2e8f0;
                }
                .gf-sidebar {
                    width: 380px;
                    display: flex;
                    flex-direction: column;
                    padding: 24px;
                    background: #fff;
                    overflow-y: auto;
                }

                /* HUD Styles */
                .gf-search-hud {
                    position: absolute;
                    top: 20px;
                    left: 60px; /* Offset from layer switch */
                    z-index: 1000;
                    width: 320px;
                }
                .locate-me-btn {
                    position: absolute;
                    top: 80px;
                    left: 12px;
                    z-index: 1000;
                    width: 34px;
                    height: 34px;
                    background: white;
                    border: 2px solid rgba(0,0,0,0.2);
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #444;
                    font-size: 18px;
                    transition: 0.2s;
                }
                .locate-me-btn:hover { background: #f4f4f4; color: #ef4444; }
                .desktop-only { display: block; }
                .mobile-only { display: none; }
                .hud-input-group {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(8px);
                    border: 1px solid #cbd5e1;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    padding: 4px 16px;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                }
                .hud-icon { color: #64748b; margin-right: 12px; }
                .hud-input-group input {
                    border: none;
                    background: transparent;
                    width: 100%;
                    padding: 10px 0;
                    font-size: 14px;
                    outline: none;
                }
                .hud-results {
                    background: white;
                    border-radius: 12px;
                    margin-top: 8px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                .hud-item {
                    padding: 12px 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    font-size: 13px;
                    transition: 0.2s;
                    border-bottom: 1px solid #f1f5f9;
                }
                .hud-item:last-child { border-bottom: none; }
                .hud-item:hover { background: #f8fafc; }
                .hud-item.empty { cursor: default; color: #94a3b8; font-style: italic; }
                .item-icon { margin-right: 12px; color: #94a3b8; flex-shrink: 0; }
                .item-text { display: flex; flex-direction: column; overflow: hidden; }
                .item-text .main { font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .item-text .secondary { font-size: 11px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

                /* Sidebar UI */
                .sidebar-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-icon {
                    width: 45px;
                    height: 45px;
                    background: #fee2e2;
                    color: #ef4444;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 15px;
                    font-size: 20px;
                }
                .sidebar-header h3 { font-size: 18px; font-weight: 700; margin: 0; color: #1e293b; }
                .sidebar-header p { font-size: 12px; color: #64748b; margin: 0; }

                .gf-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 16px;
                    margin-bottom: 16px;
                }
                .gf-card.dark { background: #1e293b; color: white; border: none; }
                .gf-card.borderless { border: none; background: #f1f5f9; }
                
                .card-header-flex {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }
                .btn-add-row {
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .btn-add-row:hover { transform: scale(1.1); background: #dc2626; }

                .coord-rows-container {
                    max-height: 200px;
                    overflow-y: auto;
                    padding-right: 4px;
                }
                .coord-rows-container::-webkit-scrollbar { width: 4px; }
                .coord-rows-container::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

                .zone-item .actions { display: flex; gap: 8px; }
                .btn-edit { border: none; background: transparent; color: #10b981; font-size: 16px; cursor: pointer; transition: 0.2s; opacity: 0.7; }
                .btn-edit:hover { opacity: 1; transform: scale(1.1); }
                .btn-del { border: none; background: transparent; color: #ef4444; font-size: 16px; cursor: pointer; transition: 0.2s; opacity: 0.7; }
                .btn-del:hover { opacity: 1; transform: scale(1.1); }

                .coord-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                    flex: 1;
                }
                .coord-row-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    position: relative;
                }
                .btn-row-remove {
                    background: transparent;
                    color: #94a3b8;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.2s;
                    margin-top: 14px; /* Align with input height */
                }
                .btn-row-remove:hover { color: #ef4444; }

                .card-label {
                    font-size: 10px;
                    font-weight: 800;
                    color: #94a3b8;
                    letter-spacing: 0.5px;
                    margin-bottom: 12px;
                    display: flex;
                    align-items: center;
                }
                .form-group { margin-bottom: 12px; }
                .form-group label { display: block; font-size: 9px; font-weight: 700; color: #64748b; margin-bottom: 4px; }
                .form-group input, .form-group select {
                    width: 100%;
                    padding: 8px 12px;
                    border-radius: 8px;
                    border: 1px solid #cbd5e1;
                    font-size: 13px;
                    outline: none;
                }

                .metric-row { display: flex; gap: 20px; }
                .metric { flex: 1; }
                .metric .label { display: block; font-size: 9px; opacity: 0.7; margin-bottom: 2px; }
                .metric .value { font-size: 16px; font-weight: 700; }
                .metric .value.coords { font-family: monospace; font-size: 12px; color: #fb7185; }

                .json-box {
                    font-family: monospace;
                    font-size: 10px;
                    height: 80px;
                    overflow-y: auto;
                    color: #475569;
                    background: #fff;
                    padding: 10px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .sidebar-footer { display: flex; flex-direction: column; gap: 10px; margin-top: auto; }
                .btn-save {
                    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
                    color: white;
                    border: none;
                    padding: 14px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.3s;
                }
                .btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
                .btn-save:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.3); }
                
                .btn-clear {
                    background: transparent;
                    color: #64748b;
                    border: 1px solid #cbd5e1;
                    padding: 10px;
                    border-radius: 10px;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.2s;
                }
                .btn-clear:hover { background: #f1f5f9; }

                .zones-list { margin-top: 24px; border-top: 1px solid #f1f5f9; padding-top: 20px; }
                .zones-list h4 { font-size: 11px; font-weight: 800; color: #94a3b8; }
                .zones-scroll { max-height: 200px; overflow-y: auto; }
                .zone-item {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    border-radius: 8px;
                    background: #f8fafc;
                    margin-bottom: 8px;
                    transition: 0.2s;
                }
                .zone-item:hover { background: #f1f5f9; }
                .dot { width: 8px; height: 8px; border-radius: 50%; margin-right: 12px; }
                .dot.active { background: #10b981; }
                .dot.inactive { background: #94a3b8; }

                /* Integrated Tabs System */
                .gf-tabs-container {
                    padding: 20px 0 40px 0;
                    background: #f8fafc;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .tabs-header {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 25px;
                    background: #fff;
                    padding: 6px;
                    border-radius: 14px;
                    width: fit-content;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.03);
                    border: 1px solid #e2e8f0;
                }
                .tab-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 20px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .tab-btn.active {
                    background: #ef4444;
                    color: white;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
                }
                .tab-btn .icon { font-size: 16px; }

                .tab-body {
                    width: 95%;
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.04);
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    min-height: 500px;
                }
                .tab-pane {
                    padding: 30px;
                }

                .table-controls {
                    padding: 20px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 20px;
                    background: #fdfdfd;
                }
                .search-box {
                    position: relative;
                    flex: 1;
                    max-width: 400px;
                }
                .search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }
                .search-box input {
                    width: 100%;
                    padding: 12px 12px 12px 42px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    font-size: 13px;
                    background: white;
                    outline: none;
                    transition: all 0.3s ease;
                }
                .search-box input:focus {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.05);
                }

                .filter-group {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }
                .filter-group.range {
                    background: #f8fafc;
                    padding: 8px 16px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .date-picker-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .filter-icon {
                    position: absolute;
                    left: 12px;
                    color: #ef4444;
                    pointer-events: none;
                }
                .date-input {
                    padding: 10px 12px 10px 38px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    outline: none;
                }

                .refresh-btn {
                    padding: 10px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .refresh-btn:hover { background: #f8fafc; color: #ef4444; }

                .table-wrapper { overflow-x: auto; }
                .modern-table.v2 th {
                    background: #f8fafc;
                    padding: 16px 20px;
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 800;
                    border-bottom: 2px solid #f1f5f9;
                    cursor: default;
                }
                .modern-table.v2 th.sortable { cursor: pointer; }
                .modern-table.v2 th.sortable:hover { background: #f1f5f9; color: #ef4444; }
                .modern-table.v2 tr:hover td { background: #fdfdfd; }
                .modern-table.v2 td { padding: 16px 20px; transition: 0.2s; }
                .text-center { text-align: center; }
                .text-left { text-align: left; }
                .text-right { text-align: right; }

                .face-capture-thumbnail {
                    width: 38px;
                    height: 38px;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 2px solid #fff;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                    background: #f8fafc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .no-face-placeholder {
                    font-size: 8px;
                    font-weight: 800;
                    color: #cbd5e1;
                    text-transform: uppercase;
                }

                .capture-bundle {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }
                .capture-nav {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #f1f5f9;
                    padding: 2px 6px;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                }
                .capture-nav button {
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 10px;
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .capture-nav button:hover { color: #ef4444; transform: scale(1.2); }

                .zoom-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.9);
                    backdrop-filter: blur(8px);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 40px;
                }
                .zoom-content {
                    position: relative;
                    max-width: 90%;
                    max-height: 90%;
                    background: white;
                    padding: 8px;
                    border-radius: 20px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                }
                .zoom-content img {
                    max-width: 100%;
                    max-height: 80vh;
                    border-radius: 12px;
                    display: block;
                }
                .close-zoom {
                    position: absolute;
                    top: -20px;
                    right: -20px;
                    width: 44px;
                    height: 44px;
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    cursor: pointer;
                    box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.4);
                    transition: 0.2s;
                    z-index: 10;
                }
                .close-zoom:hover { transform: rotate(90deg) scale(1.1); background: #dc2626; }

                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: 0.2s;
                    font-size: 14px;
                }
                .action-btn.edit { background: #f0fdf4; color: #16a34a; }
                .action-btn.edit:hover { background: #16a34a; color: white; }
                .action-btn.del { background: #fef2f2; color: #ef4444; }
                .action-btn.del:hover { background: #ef4444; color: white; }

                /* Date Navigation Pill */
                /* Date Navigation Pill */
                .date-navigation-pill {
                    display: flex;
                    align-items: center;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 50px;
                    padding: 4px 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
                    gap: 5px;
                }
                .nav-arrow {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    background: transparent;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 18px;
                    transition: 0.2s;
                    border-radius: 50%;
                }
                .nav-arrow:hover {
                    background: #f1f5f9;
                    color: #ef4444;
                }
                .date-picker-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 0 10px;
                }
                .date-label {
                    font-size: 9px;
                    font-weight: 800;
                    color: #94a3b8;
                    text-transform: uppercase;
                    margin-bottom: -2px;
                    pointer-events: none;
                }
                .date-input-box {
                    position: relative;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 2px 4px;
                    border-radius: 8px;
                    transition: 0.2s;
                }
                .date-input-box:hover {
                    background: #f8fafc;
                }
                .date-input-box input {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                    cursor: pointer;
                    z-index: 2;
                }
                .date-display {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    font-weight: 700;
                    color: #1e293b;
                    padding: 2px 0;
                }
                .cal-icon {
                    color: #ef4444;
                    font-size: 14px;
                }
                .date-separator {
                    color: #e2e8f0;
                    font-weight: 300;
                    font-size: 18px;
                    margin: 0 5px;
                }
                .refresh-btn-pill {
                    margin-left: 5px;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    border: none;
                    background: #f8fafc;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .refresh-btn-pill:hover {
                    background: #fee2e2;
                    color: #ef4444;
                }

                .modern-table.v2 th.w-16 {
                    width: 60px;
                    text-align: center;
                }
                
                .perimeter-icon-box {
                    width: 32px;
                    height: 32px;
                    background: #f1f5f9;
                    color: #64748b;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    border: 1px solid #e2e8f0;
                }
                .fence-row:hover .perimeter-icon-box {
                    background: #fee2e2;
                    color: #ef4444;
                    border-color: #fecaca;
                }

                .pagination-wrapper {
                    padding: 20px 30px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-top: 1px solid #f1f5f9;
                    background: #fdfdfd;
                }
                
                .status-badge-v2 {
                    padding: 6px 12px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .status-badge-v2.active {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #d1fae5;
                }
                .status-badge-v2.complete {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fee2e2;
                }
                
                .punch-time {
                    font-size: 11px;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 6px;
                    width: fit-content;
                }
                .punch-time.in { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }
                .punch-time.out { background: #fff1f2; color: #e11d48; border: 1px solid #ffe4e6; }
                
                .modern-table.v2 {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                }
                .modern-table.v2 tbody tr:hover {
                    background: #f8fafc;
                }
                .pagination-info { font-size: 12px; color: #64748b; font-weight: 600; }
                .pagination-btns { display: flex; gap: 5px; }
                .p-btn {
                    width: 34px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    font-size: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .p-btn:hover:not(:disabled) { border-color: #ef4444; color: #ef4444; }
                .p-btn.active { background: #ef4444; border-color: #ef4444; color: white; }
                .p-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                .sort-icon { display: inline-block; vertical-align: middle; margin-left: 4px; font-size: 10px; }

                 .punch-time {
                    padding: 3px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: 700;
                    font-family: 'JetBrains Mono', monospace;
                    display: inline-block;
                    min-width: 75px;
                    text-align: center;
                }
                .punch-time.in { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; }
                .punch-time.out { background: #fef2f2; color: #991b1b; border: 1px solid #fee2e2; }
                
                .status-badge-v2 {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 4px 12px;
                    border-radius: 20px;
                    text-transform: uppercase;
                }
                .status-badge-v2.active { background: #e0f2fe; color: #0369a1; }
                .status-badge-v2.complete { background: #f1f5f9; color: #475569; }

                .zone-item .info { flex: 1; min-width: 0; }
                .zone-item .name { display: block; font-size: 13px; font-weight: 600; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .zone-item .meta { font-size: 10px; color: #94a3b8; }
                .coord-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }
                .location-tooltip {
                    background: #1e293b !important;
                    border: 1px solid #ef4444 !important;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.3) !important;
                    border-radius: 8px !important;
                    padding: 0 !important;
                }
                .tooltip-content {
                    color: white;
                    padding: 4px 10px;
                    display: flex;
                    align-items: center;
                    font-size: 11px;
                    font-weight: 600;
                }
                .leaflet-tooltip-top:before {
                    border-top-color: #ef4444 !important;
                }
                .map-badge {
                    position: absolute;
                    bottom: 20px;
                    left: 20px;
                    background: #1e293b;
                    color: white;
                    font-size: 10px;
                    padding: 6px 12px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    letter-spacing: 1px;
                    font-weight: 700;
                    z-index: 900;
                }

                .map-popup-actions { text-align: center; padding: 4px; }
                .popup-title { font-weight: 800; font-size: 13px; color: #1e293b; margin-bottom: 10px; border-bottom: 1px solid #f1f5f9; padding-bottom: 5px; }
                .popup-btns { display: flex; gap: 8px; justify-content: center; }
                .p-btn { border: none; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 4px; transition: 0.2s; }
                .p-btn.edit { background: #3b82f6; color: white; }
                .p-btn.del { background: #fee2e2; color: #ef4444; }
                .p-btn:hover { transform: scale(1.05); }
                
                .btn-sync-coords {
                    width: 100%;
                    padding: 10px;
                    background: #fffbfa;
                    border: 2px dashed #fee2e2;
                    border-radius: 10px;
                    color: #ef4444;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    margin-top: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                .btn-sync-coords:hover {
                    background: #fff5f5;
                    border-color: #ef4444;
                    color: #ef4444;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1);
                }

                /* Responsive */
                @media (max-width: 1024px) {
                    .gf-layout { 
                        flex-direction: column; 
                        height: auto !important; 
                        min-height: auto;
                    }
                    .gf-sidebar { 
                        width: 100%; 
                        height: auto; 
                        order: 2; 
                        padding: 16px; 
                        background: #fff; 
                        z-index: 5; 
                    }
                    .gf-map-container { 
                        height: 50vh !important; 
                        min-height: 400px;
                        order: 1; 
                        border-right: none; 
                        border-bottom: 1px solid #e2e8f0; 
                        position: relative; 
                        background: #f8fafc; 
                        z-index: 10; 
                    }
                    .gf-dashboard { 
                        height: auto; 
                        min-height: 100vh; 
                        display: flex; 
                        flex-direction: column; 
                        overflow: visible; 
                    }
                    .gf-search-hud { 
                        left: auto;
                        right: 60px;
                        top: 12px;
                        transform: none;
                        width: auto !important;
                        z-index: 1001; 
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .gf-search-hud.mobile-open {
                        left: 12px;
                        right: 12px;
                    }
                    .hud-input-group {
                        padding: 0 !important;
                        border-radius: 10px !important;
                        min-width: 44px;
                        height: 44px;
                        justify-content: center;
                    }
                    .hud-toggle-btn {
                        width: 44px;
                        height: 44px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: transparent;
                        border: none;
                        color: #64748b;
                        font-size: 18px;
                        cursor: pointer;
                        flex-shrink: 0;
                    }
                    .gf-search-hud.mobile-open .hud-toggle-btn {
                        color: #ef4444;
                    }
                    .hud-input-group input {
                        padding: 0 !important;
                        width: 0;
                        opacity: 0;
                        transition: all 0.3s ease;
                        font-size: 14px !important;
                    }
                    .gf-search-hud.mobile-open .hud-input-group input {
                        width: 100%;
                        opacity: 1;
                        padding-right: 12px !important;
                    }
                    .mobile-hidden { display: none; }
                    .mobile-open .mobile-hidden { display: block; }
                    .desktop-only { display: none; }
                    .mobile-only { display: flex; }
                    
                    .locate-me-btn { 
                        left: 12px; 
                        top: 12px; 
                        z-index: 1000; 
                    }
                    .coord-grid { grid-template-columns: 1fr; gap: 8px; }
                    .metric-row { flex-direction: column; gap: 10px; }
                    .sidebar-header { margin-bottom: 20px; }
                    .leaflet-container { 
                        height: 100% !important; 
                        width: 100% !important; 
                        min-height: 400px;
                    }
                }

                @media (max-width: 480px) {
                }
            `}</style>

            {/* Zoom Modal */}
            <AnimatePresence>
                {zoomedImage && (
                    <motion.div
                        className="zoom-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setZoomedImage(null)}
                    >
                        <motion.div
                            className="zoom-content"
                            initial={{ scale: 0.5, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.5, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="close-zoom" onClick={() => setZoomedImage(null)}>
                                <FiX />
                            </button>
                            <img src={zoomedImage} alt="Zoomed Capture" />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default GeoFencingAdmin;
